<template>
  <div class="mb-5">
    <form ref="form" class="d-flex flex-column">
      <component :is="componentForField(field)" v-for="field of fields" :key="field" :ref="field" :field="field" :placeholder-text="fieldPlaceholder(field)" />

      <form-row>
        <div class="d-grid">
          <submit-button ref="submit" :label="formButtonText" :submit-in-progress="submitInProgress"
                         class="submit-button" @click="submitIfReady(submit)" />
        </div>
      </form-row>
    </form>
    <authenticate-to-publish-modal :title="i18n.t('member.events.creation.sign_in_to_publish')"
                                   :description="i18n.t('member.events.creation.sign_in_explanation')"
                                   :confirmation-warning-message="i18n.t('member.events.creation.authenticate_to_publish_modal.email_confirmation_warning')" />

    <awaiting-approval-modal v-if="displayAwaitingApprovalModal" campaign-type="event" />
  </div>
</template>

<script>
import AuthenticateToPublishModal from '@/campaign-form-shared/authenticate-to-publish-modal'
import AwaitingApprovalModal from '@/campaign-form-shared/awaiting-approval-modal'
import CalendarField from './calendar-field'
import DateTimeField from './date-time-field'
import EventTypeField from './event-type-field'
import FormRow from '@/campaign-form-shared/form-row.vue'
import ImageField from './image-field.vue'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { nextTick } from 'vue'
import { objectKeysToSnakeCase } from '@/helpers/transform-object-keys'
import PartnershipField from './partnership-field'
import RichTextField from '@/campaign-form-shared/rich-text-field'
import StaffLedCampaignField from '@/campaign-form-shared/staff-led-campaign-field'
import StringField from '@/campaign-form-shared/string-field'
import StyledVirtualInPersonSelector from './styled-virtual-in-person-selector'
import SubmitButton from '@/components/buttons/submit-button'
import WaitUntilGeocodedForSubmit from '@/mixins/wait-until-geocoded-for-submit'

export default {
  components: {
    AuthenticateToPublishModal, AwaitingApprovalModal, CalendarField, DateTimeField, EventTypeField, FormRow,
    ImageField, PartnershipField, RichTextField, StaffLedCampaignField, StringField, StyledVirtualInPersonSelector,
    SubmitButton
  },

  mixins: [ WaitUntilGeocodedForSubmit ],

  props: {
    extraParameters: {
      type: Object,
      required: true
    },
    formAction: {
      type: String,
      required: true
    },
    formButtonText: {
      type: String,
      required: true
    },
    utmParams: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      submitInProgress: false
    }
  },

  computed: {
    ...mapGetters(['dateValidationErrors', 'endAt', 'eventTypeId', 'extraLocationInfo', 'fieldHasError',
                   'hiddenAddress', 'hostAddress', 'location', 'locationValidationErrors', 'locationWithoutExtraAndVenue',
                   'maxAttendeesCount', 'partnershipId', 'pendingGeocoding', 'richDescription', 'staffLedCampaign', 'startAt',
                   'timeZone', 'title', 'unconfirmedLocation', 'virtual', 'webConferenceUrl']),
    ...mapState(['displayAwaitingApprovalModal', 'fields', 'validationErrors'])
  },

  methods: {
    ...mapMutations(['setValidationErrors']),

    componentForField(field) {
      switch (field) {
      case 'associateWithCalendar':
        return CalendarField
      case 'dateTimes':
        return DateTimeField
      case 'eventTypeId':
        return EventTypeField
      case 'image':
        return ImageField
      case 'partnershipId':
        return PartnershipField
      case 'hostAddress':
      case 'maxAttendeesCount':
      case 'title':
      case 'webConferenceUrl':
        return StringField
      case 'richDescription':
        return RichTextField
      case 'staffLedCampaign':
        return StaffLedCampaignField
      case 'virtual':
        return StyledVirtualInPersonSelector
      default:
        throw `invalid field ${field}`
      }
    },

    fieldPlaceholder(field) {
      switch (field) {
      case 'maxAttendeesCount':
        return this.i18n.t('member.events.form.max_attendees_count_placeholder')
      case 'webConferenceUrl':
        return this.i18n.t('member.events.form.web_conference_url_placeholder')
      }
    },

    scrollToFirstError() {
      for (let field of this.fields) {
        if (this.fieldHasError(field)) {
          const component = this.$refs[field][0]
          component.scrollIntoView()
          if (typeof component.focus === 'function') {
            // Only try to focus the field if it can be focused.
            component.focus()
          }
          break
        }
      }
    },

    submit: async function() {
      const { title, richDescription, timeZone, startAt, endAt, maxAttendeesCount,
              virtual, extraLocationInfo, webConferenceUrl, eventTypeId, staffLedCampaign,
              partnershipId, utmParams } = this
      const data = {
        event: {
          title,
          richDescription,
          timeZone,
          startAt,
          endAt,
          maxAttendeesCount,
          virtual,
          extraLocationInfo,
          webConferenceUrl,
          eventTypeId,
          staffLedCampaign,
          partnershipId,
          ...this.extraParameters
        }
      }

      if (utmParams) {
        data.event.utmParams = utmParams
      }

      // Add location only if it should be filled in
      if (this.location) {
        data.location = this.locationWithoutExtraAndVenue
        data.event.locationVenue = this.location.venue
      }
      if (this.hostAddress) {
        data.event.hostAddress = this.hostAddress
      }
      if (this.hiddenAddress !== null) {
        data.event.hiddenAddress = this.hiddenAddress
      }
      if (this.unconfirmedLocation !== null) {
        data.event.unconfirmedLocation = this.unconfirmedLocation
      }

      data.event.locale = this.i18n.locale()

      const result = await this.$store.dispatch(this.formAction, objectKeysToSnakeCase(data))
      if (!result) {
        nextTick(() => {
          this.scrollToFirstError()
        })
      }
      this.submitInProgress = false
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~common_styles/breakpoints" as breakpoints;

.submit-button {
  text-transform: none;
  height: 56px;
  margin-top: 100px;

  @include breakpoints.screen-mobile-only {
    height: 50px;
  }
}
</style>
